import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Flag from 'react-world-flags';


type HeaderProps = {
  title?: string;
  showBackButton?: boolean;
  showCart?: boolean;
};

export function Header({ title, showBackButton = false, showCart = true }: HeaderProps) {
  const { totalItems } = useCart();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const { hotelId } = useParams<{ hotelId: string }>();

  const handleBack = () => {
    navigate(-1);
  };

  const languageToCountryCode = {
    en: 'GB',
    es: 'ES',
    it: 'IT',
  };  

  return (
    <header className="sticky top-0 z-50 w-full bg-background border-b safe-top">
      <div className="flex h-14 items-center justify-between px-4">
        <div className="flex items-center">
          {showBackButton ? (
            <Button variant="ghost" size="icon" onClick={handleBack} className="mr-2 -ml-2">
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">Back</span>
            </Button>
          ) : null}
          
          {title ? (
            <h1 className="font-bold">{title}</h1>
          ) : (
            <Link to={`/${hotelId}`} className="font-bold">
              Smart Order
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <Flag 
              code={languageToCountryCode[language]} 
              style={{ width: 20, height: 15 }} 
            />
            <span className="sr-only">{t('language')}</span>
          </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              onClick={() => changeLanguage('en')}
              className={`flex items-center gap-2 ${language === 'en' ? 'bg-muted' : ''}`}
            >
              <Flag code="GB" style={{ width: 20, height: 15 }} />
              {t('english')}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => changeLanguage('es')}
              className={`flex items-center gap-2 ${language === 'es' ? 'bg-muted' : ''}`}
            >
              <Flag code="ES" style={{ width: 20, height: 15 }} />
              {t('spanish')}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => changeLanguage('it')}
              className={`flex items-center gap-2 ${language === 'it' ? 'bg-muted' : ''}`}
            >
              <Flag code="IT" style={{ width: 20, height: 15 }} />
              {t('italian')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

          
          {showCart && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => navigate(`/${hotelId}/cart`)}
            >
              <ShoppingBag className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}